import { z } from "zod";
import { isDateOnly } from "./dates";
import { BILLING_CYCLES, BILLING_ITEM_TYPES, CURRENCIES, PAYMENT_QR_METHODS, WEEKDAYS } from "./types";

const requiredText = (label: string, max = 200) =>
  z.string().trim().min(1, `${label} is required.`).max(max, `${label} is too long.`);
const optionalText = (max = 500) => z.string().trim().max(max, "This field is too long.");
const optionalEmail = z.string().trim().max(254).refine(
  (value) => value === "" || z.email().safeParse(value).success,
  "Enter a valid email address.",
);
const optionalDate = z.string().refine((value) => value === "" || isDateOnly(value), "Enter a valid date.");

export const addressSchema = z.object({
  line1: optionalText(200),
  line2: optionalText(200),
  city: optionalText(100),
  state: optionalText(100),
  postalCode: optionalText(30),
  country: optionalText(100),
});

export const partySchema = z.object({
  name: requiredText("Name", 150),
  company: optionalText(150),
  email: optionalEmail,
  phone: optionalText(50),
  address: addressSchema,
});

export const companySchema = partySchema.extend({
  tagline: optionalText(200),
  website: z.string().trim().max(500).refine(
    (value) => value === "" || z.url().safeParse(value).success,
    "Enter a valid website URL.",
  ),
});

export const dueRuleSchema = z.discriminatedUnion("kind", [
  z.object({ kind: z.literal("none") }),
  z.object({ kind: z.literal("upon_receipt") }),
  z.object({ kind: z.literal("specific"), date: z.string().refine(isDateOnly, "Enter a valid due date.") }),
  z.object({ kind: z.literal("net"), days: z.number().int().min(0).max(365) }),
  z.object({ kind: z.literal("weekly"), weekday: z.enum(WEEKDAYS) }),
  z.object({
    kind: z.literal("every_n_weeks"),
    intervalWeeks: z.number().int().min(1).max(52),
    anchorDate: z.string().refine(isDateOnly, "Enter a valid anchor date."),
  }),
  z.object({ kind: z.literal("monthly"), dayOfMonth: z.number().int().min(1).max(31) }),
  z.object({ kind: z.literal("custom"), label: requiredText("Custom payment terms", 300) }),
]);

export const clientDefaultsSchema = z.object({
  currency: z.enum(CURRENCIES),
  billingCycle: z.enum(BILLING_CYCLES),
  billingType: z.enum(BILLING_ITEM_TYPES),
  rateUnit: requiredText("Rate unit", 80),
  rateCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  hourlyRateCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  dueRule: dueRuleSchema,
  paymentTerms: optionalText(1000),
  paymentInstructions: optionalText(1000),
});

export const clientInputSchema = partySchema.extend({
  defaults: clientDefaultsSchema,
});

export const paymentQrSettingsSchema = z.object({
  gcashEnabled: z.boolean(),
  maribankEnabled: z.boolean(),
  preferred: z.enum(PAYMENT_QR_METHODS),
}).superRefine((settings, context) => {
  const preferredEnabled =
    settings.preferred === "gcash"
      ? settings.gcashEnabled
      : settings.maribankEnabled;

  if (
    (settings.gcashEnabled || settings.maribankEnabled) &&
    !preferredEnabled
  ) {
    context.addIssue({
      code: "custom",
      path: ["preferred"],
      message: "Choose an enabled QR payment method as preferred.",
    });
  }
});

export const invoiceItemInputSchema = z.object({
  id: requiredText("Line item ID", 100),
  description: requiredText("Description", 500),
  billingType: z.enum(BILLING_ITEM_TYPES),
  durationSeconds: z.number().int().min(0).max(100_000_000),
  hourlyRateCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  quantityMilli: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  unitLabel: optionalText(80),
  unitPriceCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
  fixedAmountCents: z.number().int().min(0).max(Number.MAX_SAFE_INTEGER),
}).superRefine((item, context) => {
  if (item.billingType === "hourly") {
    if (item.durationSeconds <= 0) context.addIssue({ code: "custom", path: ["durationSeconds"], message: "Duration must be greater than zero." });
    if (item.hourlyRateCents <= 0) context.addIssue({ code: "custom", path: ["hourlyRateCents"], message: "Hourly rate must be greater than zero." });
  }
  if (item.billingType === "quantity") {
    if (item.quantityMilli <= 0) context.addIssue({ code: "custom", path: ["quantityMilli"], message: "Quantity must be greater than zero." });
    if (item.unitPriceCents <= 0) context.addIssue({ code: "custom", path: ["unitPriceCents"], message: "Unit price must be greater than zero." });
  }
  if (item.billingType === "fixed" && item.fixedAmountCents <= 0) {
    context.addIssue({ code: "custom", path: ["fixedAmountCents"], message: "Fixed amount must be greater than zero." });
  }
});

export const invoiceInputSchema = z.object({
  clientId: z.string().nullable(),
  company: companySchema,
  client: partySchema,
  issueDate: z.string().refine(isDateOnly, "Enter a valid invoice date."),
  serviceStartDate: optionalDate,
  serviceEndDate: optionalDate,
  dueRule: dueRuleSchema,
  billingCycle: z.enum(BILLING_CYCLES),
  currency: z.enum(CURRENCIES),
  taxRateBps: z.number().int().min(0).max(10_000),
  paymentTerms: optionalText(1000),
  paymentInstructions: optionalText(1000),
  paymentQr: paymentQrSettingsSchema,
  publicNotes: optionalText(3000),
  internalNotes: optionalText(3000),
  items: z.array(invoiceItemInputSchema).min(1, "Add at least one line item.").max(100),
}).superRefine((invoice, context) => {
  const hasStart = invoice.serviceStartDate !== "";
  const hasEnd = invoice.serviceEndDate !== "";
  if (hasStart !== hasEnd) {
    context.addIssue({ code: "custom", path: hasStart ? ["serviceEndDate"] : ["serviceStartDate"], message: "Enter both service coverage dates, or leave both blank." });
  }
  if (hasStart && hasEnd && invoice.serviceEndDate < invoice.serviceStartDate) {
    context.addIssue({ code: "custom", path: ["serviceEndDate"], message: "Service end date cannot be before the start date." });
  }
});

export const paymentInputSchema = z.object({
  paidAt: z.string().refine(isDateOnly, "Enter a valid payment date."),
  method: requiredText("Payment method", 100),
  reference: optionalText(200),
});

export const shareInputSchema = z.object({
  enabled: z.boolean(),
  regenerate: z.boolean(),
  expiresAt: z.string().nullable().refine((value) => value === null || value === "" || isDateOnly(value), "Enter a valid expiry date."),
  pin: z.string().nullable().refine((value) => value === null || value === "" || /^\d{4,12}$/.test(value), "PIN must contain 4 to 12 digits."),
});

export const invoiceMetadataPatchSchema = z.object({
  client: partySchema.optional(),
  serviceStartDate: optionalDate.optional(),
  serviceEndDate: optionalDate.optional(),
  publicNotes: optionalText(3000).optional(),
  paymentQr: paymentQrSettingsSchema.optional(),
}).superRefine((value, context) => {
  if (value.serviceStartDate !== undefined || value.serviceEndDate !== undefined) {
    const start = value.serviceStartDate ?? "";
    const end = value.serviceEndDate ?? "";
    if (Boolean(start) !== Boolean(end)) {
      context.addIssue({ code: "custom", path: start ? ["serviceEndDate"] : ["serviceStartDate"], message: "Enter both coverage dates, or leave both blank." });
    }
    if (start && end && end < start) {
      context.addIssue({ code: "custom", path: ["serviceEndDate"], message: "End date cannot be before the start date." });
    }
  }
});

export type InvoiceInputValues = z.infer<typeof invoiceInputSchema>;
export type ClientInputValues = z.infer<typeof clientInputSchema>;
export type PaymentInputValues = z.infer<typeof paymentInputSchema>;
export type ShareInputValues = z.infer<typeof shareInputSchema>;

const qrDataUrlSchema = z.string().max(1_500_000, "QR image is too large.").refine(
  (value) => /^data:image\/(png|jpeg|webp);base64,[A-Za-z0-9+/=]+$/.test(value),
  "Upload a PNG, JPEG, or WebP QR image.",
);

const workspaceQrInputSchema = z.object({
  accountLabel: optionalText(200),
  imageDataUrl: qrDataUrlSchema.nullable().optional(),
  aspectRatio: z.number().min(0.2).max(5),
});

export const workspaceSettingsInputSchema = z.object({
  workspaceName: requiredText("Workspace name", 80),
  company: companySchema,
  defaultCurrency: z.enum(CURRENCIES),
  invoicePrefix: z.string().trim().toUpperCase().regex(/^[A-Z0-9]{2,12}$/, "Use 2 to 12 letters or numbers."),
  invoicePadding: z.number().int().min(3).max(6),
  paymentQr: z.object({
    gcash: workspaceQrInputSchema,
    maribank: workspaceQrInputSchema,
  }),
});

export const provisionWorkspaceSchema = z.object({
  name: requiredText("Workspace name", 80),
  ownerEmail: z.string().trim().toLowerCase().max(254).pipe(z.email("Enter a valid owner email.")),
  defaultCurrency: z.enum(CURRENCIES),
});

export const updatePlatformWorkspaceSchema = z.object({
  name: requiredText("Workspace name", 80),
  ownerEmail: z.string().trim().toLowerCase().max(254).pipe(z.email("Enter a valid owner email.")),
});

export const setPlatformWorkspaceActiveSchema = z.object({
  active: z.boolean(),
});
